import { useChatEditorCommunicator } from "@/features/AI/KaiChat/hooks/useChatEditorCommunicator";
import { useBreakPoints } from "@/hooks/styles/useBreakpoints";
import { createContext, useContextSelector } from "@knowt/syncing/utils/use-context-selector";
import React, { useCallback, useEffect, useState } from "react";
import type { SidebarContextType } from "../types";
import { ChatAndMediaTab } from "@/features/AI/ChatAndMediaTabbedView/ChatAndMediaTabbedView";

export const DEFAULT_SIDEBAR_WIDTH = 380;
export const MAX_SIDEBAR_WIDTH = 500;
export const MIN_SIDEBAR_WIDTH = 380;

const SidebarContext = createContext<SidebarContextType>({});

export const SidebarContextProvider = ({
    children,
    noteId,
}: {
    children: React.ReactNode;
    mediaId?: string;
    noteId?: string;
}) => {
    const { mdDown } = useBreakPoints();

    const [isNoteSidebarCollapse, setIsNoteSidebarCollapse] = useState(false);
    const [isNoteChatSplitOpen, setIsChatSplitOpen_] = useState(false);
    const [chatMediaSectionTab, setChatMediaSectionTab] = useState<ChatAndMediaTab>(ChatAndMediaTab.CHAT);

    const setIsNoteChatSplitOpen = useCallback(
        (value: boolean) => {
            setIsChatSplitOpen_(value);
        },
        [noteId, mdDown]
    );

    const stagePrompt = useChatEditorCommunicator(s => s.stagePrompt);
    const handleUnselect = useChatEditorCommunicator(s => s.handleUnselect);
    const focusToChatInput = useChatEditorCommunicator(state => state.focusToChatInput);

    const toggleNoteSidebar = useCallback(() => {
        if (isNoteChatSplitOpen) return setIsNoteChatSplitOpen(false);
        setIsNoteSidebarCollapse(!isNoteSidebarCollapse);
    }, [isNoteSidebarCollapse, isNoteChatSplitOpen, setIsNoteChatSplitOpen]);

    const collapseNoteSidebar = useCallback(() => {
        if (!isNoteSidebarCollapse) toggleNoteSidebar();
    }, [isNoteSidebarCollapse, toggleNoteSidebar]);

    // on `mdDown` close sidebar by default
    useEffect(() => {
        if (mdDown && isNoteSidebarCollapse) {
            collapseNoteSidebar();
        }
    }, [mdDown, collapseNoteSidebar, isNoteSidebarCollapse]);

    const toggleNoteChatSplit = useCallback(
        (props?: { isMediaTabOpen?: boolean }) => {
            if (isNoteChatSplitOpen) {
                setIsNoteChatSplitOpen(false);
                handleUnselect();
                stagePrompt({ prompt: "", inputQuestion: "" });
                focusToChatInput();
                return;
            }

            setChatMediaSectionTab(props?.isMediaTabOpen ? ChatAndMediaTab.MEDIA : ChatAndMediaTab.CHAT);
            setIsNoteChatSplitOpen(true);
        },
        [isNoteChatSplitOpen, handleUnselect, stagePrompt, focusToChatInput, setIsNoteChatSplitOpen]
    );

    return (
        <SidebarContext.Provider
            value={{
                isNoteSidebarCollapse: isNoteSidebarCollapse || isNoteChatSplitOpen, // only show sidebar when not in chat split
                toggleNoteSidebar,
                collapseNoteSidebar,
                toggleNoteChatSplit,
                setIsNoteChatSplitOpen,
                isNoteChatSplitOpen,
                chatMediaSectionTab,
            }}>
            {children}
        </SidebarContext.Provider>
    );
};

export const useSidebarContextSelector = <T,>(selector: (context: SidebarContextType) => T) =>
    useContextSelector(SidebarContext, selector);
